import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Clock } from '@phosphor-icons/react';
import { adminAPI } from '../../lib/api';
import ConfirmDialog from '../events/ConfirmDialog';

// "Awaiting Approval" — new member sign-ups pending admin approve/deny.
// Renders at the top of the Members tab ONLY when there are pending
// registrations; hidden entirely otherwise. Preserves the original approval
// workflow + data (adminAPI.getPendingUsers / approveUser / denyUser).
export default function AwaitingApprovalSection({ onChanged }) {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);
  const [confirm, setConfirm] = useState(null); // { profileId, action, name }
  const [reasons, setReasons] = useState({});

  const fetchPending = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getPendingUsers();
      setPending(res.data.pending_users || []);
    } catch {
      toast.error('Could not load pending registrations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPending(); }, [fetchPending]);

  const handleConfirm = async () => {
    if (!confirm) return;
    setActing(confirm.profileId);
    try {
      if (confirm.action === 'approve') {
        const res = await adminAPI.approveUser(confirm.profileId);
        toast.success(`${confirm.name} approved${res.data?.rank ? ` — ranked #${res.data.rank}` : ''}.`);
      } else {
        await adminAPI.denyUser(confirm.profileId, (reasons[confirm.profileId] || '').trim() || null);
        toast.success(`${confirm.name}'s registration denied.`);
      }
      await fetchPending();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setActing(null);
      setConfirm(null);
    }
  };

  // Hidden completely when nothing is pending (and once loaded).
  if (loading || pending.length === 0) return null;

  return (
    <div className="space-y-3" data-testid="awaiting-approval-section">
      <div className="flex items-center gap-2">
        <Clock size={18} weight="duotone" className="text-[#c9a93a]" />
        <h2 className="font-serif text-xl text-[#013523] tracking-tight">Awaiting approval</h2>
        <span className="text-[11px] font-semibold bg-[#fbf2d6] text-[#8a6d12] rounded-full px-2 py-0.5"
              data-testid="awaiting-approval-count">
          {pending.length}
        </span>
      </div>

      <ul className="space-y-3">
        {pending.map((p) => {
          const fullName = `${p.first_name || ''} ${p.last_name || ''}`.trim() || p.email;
          const submitted = p.created_at ? new Date(p.created_at).toLocaleString() : '—';
          const consent = p.sms_consent_timestamp ? new Date(p.sms_consent_timestamp).toLocaleString() : '—';
          return (
            <li key={p.id} data-testid={`pending-row-${p.id}`}
                className="bg-white border border-[#e3dcd1] rounded-2xl p-4 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-medium text-[#013523]">{fullName}</div>
                  <div className="text-xs text-[#6a766e]">{p.email}</div>
                  {p.phone && <div className="text-xs text-[#6a766e]">{p.phone}</div>}
                </div>
                <div className="shrink-0 flex flex-col items-end gap-1.5">
                  {p.usta_rating && (
                    <span className="text-[10px] uppercase tracking-[0.18em] bg-[#f0ece3] text-[#013523] rounded-full px-2.5 py-1">
                      USTA {p.usta_rating}
                    </span>
                  )}
                  {p.gender && (
                    <span data-testid={`pending-gender-${p.id}`}
                          className="text-[10px] uppercase tracking-[0.18em] bg-[#013523]/10 text-[#013523] rounded-full px-2.5 py-1">
                      {p.gender === 'male' ? 'Male' : 'Female'}
                    </span>
                  )}
                </div>
              </div>

              <dl className="text-[11px] text-[#6a766e] grid grid-cols-2 gap-x-4 gap-y-0.5">
                <dt className="opacity-70">Submitted</dt><dd>{submitted}</dd>
                <dt className="opacity-70">SMS consent</dt><dd>{p.sms_consent ? `✓ ${consent}` : '—'}</dd>
                <dt className="opacity-70">Terms</dt><dd>{p.terms_accepted_timestamp ? '✓ Accepted' : '—'}</dd>
                <dt className="opacity-70">IP</dt><dd className="font-mono">{p.registration_ip_address || '—'}</dd>
                {p.consent_version && (<><dt className="opacity-70">Consent v</dt><dd className="font-mono">{p.consent_version}</dd></>)}
              </dl>

              <div className="flex flex-col gap-2 pt-1">
                <input
                  data-testid={`deny-reason-${p.id}`}
                  type="text"
                  placeholder="Optional denial reason (internal only)"
                  value={reasons[p.id] || ''}
                  onChange={(e) => setReasons((m) => ({ ...m, [p.id]: e.target.value }))}
                  className="w-full bg-white border border-[#e3dcd1] rounded-lg px-3 py-2 text-xs text-[#013523] placeholder-[#9aa39d] focus:outline-none focus:ring-2 focus:ring-[#015e3f]/30"
                />
                <div className="flex gap-2">
                  <button
                    data-testid={`approve-${p.id}`}
                    disabled={acting === p.id}
                    onClick={() => setConfirm({ profileId: p.id, action: 'approve', name: fullName })}
                    className="flex-1 bg-[#015e3f] hover:bg-[#014d34] text-white rounded-full px-4 py-2 text-sm font-medium disabled:opacity-50">
                    Approve
                  </button>
                  <button
                    data-testid={`deny-${p.id}`}
                    disabled={acting === p.id}
                    onClick={() => setConfirm({ profileId: p.id, action: 'deny', name: fullName })}
                    className="flex-1 bg-white border border-[#e3dcd1] text-[#013523] hover:bg-[#f0ece3] rounded-full px-4 py-2 text-sm font-medium disabled:opacity-50">
                    Deny
                  </button>
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(v) => { if (!v) setConfirm(null); }}
        title={confirm?.action === 'approve' ? `Approve ${confirm?.name}?` : `Deny ${confirm?.name}?`}
        description={
          confirm?.action === 'approve'
            ? 'They will be added to the bottom of the ladder and gain immediate access.'
            : ((reasons[confirm?.profileId] || '').trim()
                ? `Reason: "${(reasons[confirm?.profileId] || '').trim()}". They will not be able to access the ladder.`
                : 'They will not be able to access the ladder.')
        }
        confirmLabel={confirm?.action === 'approve' ? 'Approve' : 'Deny'}
        destructive={confirm?.action === 'deny'}
        busy={acting === confirm?.profileId}
        onConfirm={handleConfirm}
      />
    </div>
  );
}
